import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy } from '@angular/core';
import { IModalComponent } from '@lib/modal';
import { CsvParserService } from '@core/services';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { FormBuilder } from '@angular/forms';
import { FormHelpers, IRowValidationResult } from '@core/helpers';
import { DoctorInviteUtils, IDoctorInviteFormModel } from '../doctor-invite.common';
import { TranslateService } from '@project/translate';
import { NotificationsService } from '@lib/notifications';
import { EDoctorSpecializationDTO, EGenderDTO } from '@project/view-models';
import { HcService } from '@project/services';

@Component({
  selector: 'app-doctor-invite-import-modal',
  templateUrl: './doctor-invite-import-modal.component.html',
  styleUrls: ['./doctor-invite-import-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoctorInviteImportModalComponent implements IModalComponent, OnDestroy {
  close$ = new EventEmitter<void>();
  submit$ = new EventEmitter();

  private _validItems$ = new BehaviorSubject<IRowValidationResult<IDoctorInviteFormModel>[]>([]);
  public validItems$ = this._validItems$.asObservable();

  private _invalidItems$ = new BehaviorSubject<IRowValidationResult<IDoctorInviteFormModel>[]>([]);
  public invalidItems$ = this._invalidItems$.asObservable();

  public readonly isPublicHc = this.hcService.getHcConfig().isPublic;

  public readonly doctorTemplateCsvHref = this.isPublicHc
    ? 'assets/templates/public-hc-doctors-invite-template.csv'
    : 'assets/templates/private-hc-doctors-invite-template.csv';

  public readonly columnLabelsMap: { [K in keyof IDoctorInviteFormModel]: string } = {
    firstName: TranslateService.localize('nouns.first-name'),
    lastName: TranslateService.localize('nouns.last-name'),
    email: TranslateService.localize('nouns.email'),
    gender: TranslateService.localize('nouns.gender'),
    specialization: TranslateService.localize('nouns.specialization'),
    phone: TranslateService.localize('nouns.phone'),
    dateOfBirth: TranslateService.localize('nouns.date-of-birth'),
    cpf: TranslateService.localize('nouns.cpf-number'),
    cns: TranslateService.localize('nouns.cns'),
  };

  private readonly destroy$ = new Subject();

  constructor(
    private csvParserService: CsvParserService,
    private formBuilder: FormBuilder,
    private notificationsService: NotificationsService,
    private changeDetectorRef: ChangeDetectorRef,
    private hcService: HcService,
  ) {}

  close() {
    this.close$.emit();
  }

  doSuccess() {
    this.submit$.next(this._validItems$.value.map((record) => DoctorInviteUtils.createDoctorInviteDto(record.record)));
  }

  onSelectFile(event: Event) {
    this._invalidItems$.next([]);
    this._validItems$.next([]);

    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files[0];
    if (!file) return;

    const formGroup = this.getFormGroupByHcType(this.isPublicHc);
    const fieldsToParse = this.getFormFieldsToParseByHcType(this.isPublicHc);
    console.log({ formGroup, fieldsToParse });

    this.csvParserService
      .parseFile(file, fieldsToParse)
      .pipe(
        map((records) => this.mapFormFieldsByHcType(records, this.isPublicHc)),
        map((records) => FormHelpers.validateRecords(records, formGroup)),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (validatedResult: IRowValidationResult<IDoctorInviteFormModel>[]) => {
          let invalid: IRowValidationResult<IDoctorInviteFormModel>[] = [];
          let valid: IRowValidationResult<IDoctorInviteFormModel>[] = [];

          invalid = validatedResult.filter((validatedRecord) => !validatedRecord.isValid);
          valid = validatedResult
            .filter((validatedRecord) => validatedRecord.isValid)
            .map((validatedRecord) => validatedRecord);

          const res = [];
          validatedResult.map((item: IRowValidationResult<IDoctorInviteFormModel>) => {
            const existItem = res.find((x) => x.record.email === item.record.email);
            if (existItem) {
              invalid.push(item);
            }
            res.push(item);
          }, []);

          invalid = invalid.filter((v, i, a) => a.findIndex((t) => t.record.email === v.record.email) === i);

          valid = valid.filter((v) => invalid.find((r) => r) !== v);

          this._validItems$.next(valid);
          this._invalidItems$.next(invalid);
        },
        (err) => {
          console.warn(err);
          this.notificationsService.error({
            message: TranslateService.localize('import-invites.errors.parse-file-error'),
          });

          fileInput.value = null;
          this.changeDetectorRef.markForCheck();
        },
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  private getFormGroupByHcType(isPublicHc: boolean) {
    const formDeclaration = isPublicHc
      ? DoctorInviteUtils.getPublicHcDoctorInviteFromDeclarationImport(this.formBuilder)
      : DoctorInviteUtils.getPrivateHcDoctorInviteFromDeclarationImport(this.formBuilder);
    console.log({ formDeclaration });
    return this.formBuilder.group(formDeclaration);
  }

  private getFormFieldsToParseByHcType(isPublicHc: boolean) {
    const fields = ['firstName', 'lastName', 'email', 'gender', 'specialization', 'phone', 'dateOfBirth', 'cpf', 'cns'];
    const publicHcOnlyFields = ['cns'];

    return isPublicHc ? fields : fields.filter((f) => !publicHcOnlyFields.includes(f));
  }

  private mapFormFieldsByHcType(records: Record<string, string>[], isPublicHc: boolean): IDoctorInviteFormModel[] {
    return records.map((record) => ({
      firstName: record.firstName.trim(),
      lastName: record.lastName.trim(),
      email: record.email.trim(),
      gender: EGenderDTO[record.gender.trim()] ?? record.gender.trim(),
      specialization: EDoctorSpecializationDTO[record.specialization.trim()] ?? record.specialization.trim(),
      phone: record.phone.trim(),
      dateOfBirth: record.dateOfBirth.trim().replace(/[/]/g, '.'),
      cpf: record.cpf.trim(),
      ...(isPublicHc && { cns: record.cns.trim() }),
    }));
  }
}
